import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { DataGrid } from '@mui/x-data-grid';
import { trTR } from '@mui/x-data-grid/locales';
import DownloadIcon from '@mui/icons-material/Download';
import ExcelFileCreator from 'src/components/ExcelFileCreator';

import SectionCard from 'src/components/BpoMutabakat/SectionCard';
import InfoNote from 'src/components/BpoMutabakat/InfoNote';
import CallFunnel from 'src/components/BpoMutabakat/CallFunnel';
import BpoBreakdown from 'src/components/BpoMutabakat/BpoBreakdown';
import LevelBadge from 'src/components/BpoMutabakat/LevelBadge';
import { getSeriesColors } from 'src/components/BpoMutabakat/colors';
import { reportName } from 'src/components/BpoMutabakat/reports';
import {
  formatDuration, formatNumber, formatPercent, getBpoVendor, intervalStart, ratio
} from 'src/components/BpoMutabakat/format';

const Overview = ({ date, summary, intervals, channels, queues, loading }) => {
  const theme = useTheme();
  const colors = getSeriesColors(theme);

  /* ----------------------------------------- gün içi çağrı dağılımı (RT 15) */
  const intervalChart = useMemo(() => {
    const slots = intervals || [];
    return {
      series: [
        { name: 'Gelen çağrı', type: 'area', data: slots.map((r) => r.incoming) },
        { name: 'Cevaplanan', type: 'line', data: slots.map((r) => r.answered) },
        { name: 'Servis seviyesi 80/20', type: 'line', data: slots.map((r) => r.sl) }
      ],
      options: {
        chart: {
          height: 330,
          type: 'line',
          toolbar: { show: false },
          animations: { enabled: false },
          fontFamily: theme.typography.fontFamily,
          background: 'transparent'
        },
        theme: { mode: theme.palette.mode },
        colors: [colors[0], theme.palette.mode === 'dark' ? '#FFB74D' : '#E68900', '#2E7D32'],
        stroke: { width: [1.5, 2, 1.5], curve: 'straight', dashArray: [0, 0, 4] },
        fill: { type: ['gradient', 'solid', 'solid'], opacity: [0.22, 1, 1] },
        markers: { size: 0, hover: { size: 4 } },
        dataLabels: { enabled: false },
        grid: { borderColor: theme.palette.divider, strokeDashArray: 3 },
        legend: { position: 'bottom', horizontalAlign: 'left', fontSize: '12px' },
        xaxis: {
          categories: slots.map((r) => r.slot),
          tickAmount: 12,
          labels: {
            rotate: 0,
            style: { fontSize: '11px' },
            formatter: (v) => intervalStart(v)
          },
          axisTicks: { show: false }
        },
        yaxis: [
          {
            seriesName: 'Gelen çağrı',
            title: { text: 'Çağrı', style: { fontWeight: 400, fontSize: '11px' } },
            labels: { formatter: (v) => formatNumber(Math.round(v)) }
          },
          { seriesName: 'Gelen çağrı', show: false },
          {
            opposite: true,
            min: 0,
            max: 100,
            title: { text: 'Servis seviyesi', style: { fontWeight: 400, fontSize: '11px' } },
            labels: { formatter: (v) => `%${Math.round(v)}` }
          }
        ],
        tooltip: {
          theme: theme.palette.mode,
          shared: true,
          intersect: false,
          y: {
            formatter: (v, { seriesIndex }) =>
              v === null ? 'çağrı yok' : seriesIndex === 2 ? formatPercent(v) : formatNumber(v)
          }
        }
      }
    };
  }, [intervals, theme, colors]);

  /* --------------------------------------------- çağrı geliş kanalı (RT 14) */
  const channelChart = useMemo(() => {
    const items = channels || [];
    return {
      series: items.map((c) => c.count),
      options: {
        chart: {
          type: 'donut',
          fontFamily: theme.typography.fontFamily,
          background: 'transparent'
        },
        theme: { mode: theme.palette.mode },
        labels: items.map((c) => c.channel),
        colors,
        stroke: { width: 0 },
        dataLabels: { enabled: false },
        legend: { position: 'bottom', fontSize: '12px' },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                value: { fontSize: '20px', fontWeight: 500, formatter: (v) => formatNumber(v) },
                total: {
                  show: true,
                  label: 'Toplam çağrı',
                  fontSize: '12px',
                  formatter: (w) =>
                    formatNumber(w.globals.seriesTotals.reduce((a, b) => a + b, 0))
                }
              }
            }
          }
        },
        tooltip: { theme: theme.palette.mode, y: { formatter: (v) => formatNumber(v) } }
      }
    };
  }, [channels, theme, colors]);

  /* ------------------------------------------------------ kuyruk tablosu */
  const columns = [
    { field: 'queue', headerName: 'Kuyruk', flex: 1.6, minWidth: 220 },
    {
      field: 'vendor',
      headerName: 'BPO',
      width: 100,
      valueGetter: (value, row) => getBpoVendor(row.queue),
      renderCell: (params) => (
        <Box
          component="span"
          sx={(t) => ({
            px: 0.8,
            py: '2px',
            borderRadius: 0.75,
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
            color: 'text.secondary',
            backgroundColor:
              t.palette.mode === 'dark' ? 'rgba(255,255,255,.07)' : t.palette.grey[100]
          })}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'incoming',
      headerName: 'Gelen',
      type: 'number',
      width: 92,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'answered',
      headerName: 'Cevaplanan',
      type: 'number',
      width: 112,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'abandoned',
      headerName: 'Kaçan',
      type: 'number',
      width: 86,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'answerRate',
      headerName: 'Cevaplama',
      type: 'number',
      width: 124,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <LevelBadge value={params.value} />
    },
    {
      field: 'sl',
      headerName: 'SL %80/20',
      type: 'number',
      width: 124,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <LevelBadge value={params.value} />
    },
    {
      field: 'avgAnswerTime',
      headerName: 'Ort. karşılama',
      type: 'number',
      width: 132,
      valueFormatter: (v) => `${v} sn`
    },
    {
      field: 'aht',
      headerName: 'AHT',
      type: 'number',
      width: 90,
      valueFormatter: (v) => formatDuration(v)
    }
  ];

  const exportToExcel = () => {
    ExcelFileCreator(
      (queues || []).map((q) => ({
        Kuyruk: q.queue,
        BPO: getBpoVendor(q.queue),
        Gelen: q.incoming,
        Cevaplanan: q.answered,
        Kaçan: q.abandoned,
        'Cevaplama %': q.answerRate,
        'SL 80/20 %': q.sl,
        'Ort. karşılama sn': q.avgAnswerTime,
        'AHT sn': q.aht
      })),
      `BPO Mutabakat - Kuyruk Ozeti ${date}`
    );
  };

  return (
    <Grid container spacing={2}>
      {/* --------------------------------------------------- huni + BPO */}
      <Grid size={{ xs: 12, lg: 6 }}>
        <SectionCard title="Çağrı hunisi" subtitle="santralden agent’a">
          <CallFunnel summary={summary} loading={loading} />
          <InfoNote sx={{ mt: 2 }}>
            Santrale gelen {formatNumber(summary?.switchboardCalls)} çağrının{' '}
            <b>
              {formatPercent(ratio(summary?.queuedCalls, summary?.switchboardCalls))}
            </b>
            ’si ACD kuyruğuna düşüyor. Kalanı IVR’da kendi kendine çözülüyor ya da kuyruğa
            varmadan kapanıyor. Faturalanan hacim kuyruğa düşen taraftır &mdash; bu iki sayı
            karıştırılırsa fark beş katına çıkar.
          </InfoNote>
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 6 }}>
        <SectionCard
          title="BPO firma kırılımı"
          subtitle={`kuyruk adı son ekinden · ${reportName(1)}`}
        >
          <BpoBreakdown queues={queues} loading={loading} />
          <InfoNote sx={{ mt: 2 }}>
            Kuyruk adları <code>_ASSISTT</code>, <code>_MEGA</code>, <code>_CMC</code> ile
            bitiyor; hizmeti veren firma bu ekte. Servis seviyesi belirgin şekilde ayrışan firma
            varsa mutabakat toplantısının ilk maddesi odur.
          </InfoNote>
        </SectionCard>
      </Grid>

      {/* ------------------------------------------- interval + kanal donutu */}
      <Grid size={{ xs: 12, lg: 8 }}>
        <SectionCard
          title="Gün içi çağrı dağılımı"
          subtitle={`${reportName(15)} · 30 dk dilim`}
        >
          {loading ? (
            <Skeleton variant="rounded" height={330} />
          ) : (
            <ReactApexChart
              options={intervalChart.options}
              series={intervalChart.series}
              type="line"
              height={330}
            />
          )}
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 4 }}>
        <SectionCard title="Çağrı geliş kanalı" subtitle={reportName(14)}>
          {loading ? (
            <Skeleton variant="rounded" height={330} />
          ) : (
            <ReactApexChart
              options={channelChart.options}
              series={channelChart.series}
              type="donut"
              height={330}
            />
          )}
          <InfoNote sx={{ mt: 1 }}>
            Kanal toplamı, {reportName(13)} raporunun satır sayısıyla birebir tutuyor
            &mdash; özet ile detay rapor uyuşuyor.
          </InfoNote>
        </SectionCard>
      </Grid>

      {/* ---------------------------------------------------- kuyruk tablosu */}
      <Grid size={12}>
        <SectionCard
          title="Kuyruk özeti"
          subtitle={`${reportName(1)} · ${(queues || []).length} kuyruk`}
          action={
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={loading || !(queues || []).length}
            >
              Excel
            </Button>
          }
          padded={false}
        >
          <Box sx={{ height: 520, width: '100%' }}>
            <DataGrid
              rows={queues || []}
              columns={columns}
              loading={loading}
              density="compact"
              disableRowSelectionOnClick
              localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50]}
              sx={{ border: 0 }}
            />
          </Box>
        </SectionCard>
      </Grid>

      {summary && (
        <Grid size={12}>
          <Typography variant="caption" color="text.secondary">
            Agent tarafı: {formatNumber(summary.agentCount)} agent,{' '}
            {formatNumber(summary.agentAcdCalls)} ACD çağrı, {formatDuration(summary.aht)}{' '}
            ortalama AHT, {formatNumber(Math.round(summary.staffedHours))} saat açık süre.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

Overview.propTypes = {
  date: PropTypes.string,
  summary: PropTypes.object,
  intervals: PropTypes.array,
  channels: PropTypes.array,
  queues: PropTypes.array,
  loading: PropTypes.bool
};

export default Overview;
